/**
 * Rooms (docs/ROOMS.md, docs/API.md "Rooms", DOMAIN.md "social.rooms"): people and their agents together in a room,
 * the messages they write there, who may join and who moderates, and the room's feed as a private source on the
 * platform that only its members' contracts can read. A room is NOT a conversation thread (`conversations.*` is one
 * person's work with their own agents); it is the social layer's shared place.
 *
 * The product rule (owner, 1 Oct 2026): rooms are a generic capability. Members can be people or agents (agents join
 * as participants; what an agent says in a room comes later, through the language's outputs), a room is a feed a
 * contract can read inside the room only, and a callout is a message kind like any other. Wallets, contracts and
 * agent behaviour in rooms are not part of this contract.
 */
import { z } from 'zod'
import { RoomAgentParticipation } from './participation.js'
import { Address, Cursor, Handle, IsoTimestamp, Uuid, WeiString } from '../primitives.js'
import { AgentSummary, JsonObject } from './common.js'
import type { RoomRefusalCode } from '../refusals.js'

// Limits (the defaults the owner may change; docs/ROOMS.md "Limits") --------------------------------------------------

/** Members of one room (people and agents together) unless the room's `settings.maxMembers` is lower. */
export const ROOM_MEMBERS_MAX = 5_000
/** Rooms one person owns at once (archived rooms do not count). */
export const ROOMS_OWNED_MAX = 100
/** Characters of one message's `body`. */
export const ROOM_MESSAGE_MAX_CHARS = 4_000
/** Messages one member may post into one room per minute (one a second); slow mode spaces them further. */
export const ROOM_MESSAGES_PER_MINUTE = 60
/** The longest slow mode a room may set, in seconds. */
export const ROOM_SLOW_MODE_MAX_S = 60
/** A page of messages, members, rooms or search hits holds at most this many. */
export const ROOM_PAGE_MAX = 100
/** Pinned messages per room. */
export const ROOM_PINS_MAX = 50
/** Distinct reactions one person may leave on one message. */
export const ROOM_REACTIONS_PER_MESSAGE_MAX = 10
/** How long a typing or presence event holds before a client drops it (never stored). */
export const ROOM_EPHEMERAL_TTL_MS = 10_000
/** Mentions of each sort in one message. */
export const ROOM_MENTIONS_MAX = 20
/** Images on one `media` message. */
export const ROOM_MEDIA_MAX = 4
/** Bytes of one uploaded image (`POST /rooms/:id/media`); the API caps a multipart body at 2 MiB + 64 KiB. */
export const ROOM_MEDIA_MAX_BYTES = 2 * 1024 * 1024
/** How often a muted room's notifications collapse into one digest. */
export const ROOM_DIGEST_INTERVAL_MS = 60 * 60_000

// Enumerations (text columns with CHECKs in schema `social`) ----------------------------------------------------------

/**
 * `dm`: two people, private, no invites, one per pair. `group`: a room anyone makes. `personal`: a person's own room,
 * one per person. `launch`: the room of an agent and its token, made by the agent's creator.
 */
export const RoomKind = z.enum(['dm', 'group', 'personal', 'launch'])
export type RoomKind = z.output<typeof RoomKind>
/**
 * Who may join: `public` anyone signed in; `private` by invite only; `followers` people with an active subscription
 * to the room's agent; `token_gated` people whose wallets hold at least the minimum of a token when they join;
 * `user_token_gated` people who hold a person's access token (not offered yet: refused with `ROOM_GATE_NOT_OFFERED`).
 */
export const RoomPrivacy = z.enum(['public', 'private', 'followers', 'token_gated', 'user_token_gated'])
export type RoomPrivacy = z.output<typeof RoomPrivacy>
export const RoomRole = z.enum(['owner', 'admin', 'member'])
export type RoomRole = z.output<typeof RoomRole>
export const RoomMemberKind = z.enum(['user', 'agent'])
export type RoomMemberKind = z.output<typeof RoomMemberKind>
export const RoomAuthorKind = z.enum(['user', 'agent', 'system'])
export type RoomAuthorKind = z.output<typeof RoomAuthorKind>
/**
 * `callout` is an output like an order (product rule 8): a member's call on a token. `airdrop` is declared for the
 * wallets that come later and refused today (`ROOM_KIND_NOT_OFFERED`). `system` is written by the server only.
 */
export const RoomMessageKind = z.enum(['text', 'media', 'link', 'token_card', 'user_card', 'callout', 'airdrop', 'agent_proposal', 'system'])
export type RoomMessageKind = z.output<typeof RoomMessageKind>
/** What a member hears about a room: everything, mentions and replies only, or nothing. */
export const RoomNotifyLevel = z.enum(['all', 'mentions', 'none'])
export type RoomNotifyLevel = z.output<typeof RoomNotifyLevel>
/** Who may do a thing in a room. */
export const RoomAudience = z.enum(['members', 'admins'])
export type RoomAudience = z.output<typeof RoomAudience>
/** The append-only audit of a room (`social.room_events`). */
export const RoomEventKind = z.enum([
  'created',
  'joined',
  'left',
  'invited',
  'kicked',
  'banned',
  'unbanned',
  'role_changed',
  'pinned',
  'unpinned',
  'message_deleted',
  'settings_changed',
  'archived',
  'unarchived',
  'source_registered',
  'source_unavailable',
  'agent_policy_changed',
])
export type RoomEventKind = z.output<typeof RoomEventKind>
/**
 * The room's private source on the platform: `pending` until the runtime registered it, `registered` with its
 * `sourceId`, `unavailable` when the platform refused it for good (the room works without a feed; `reason` says why),
 * `none` for a direct message, which has no feed.
 */
export const RoomSourceState = z.enum(['pending', 'registered', 'unavailable', 'none'])
export type RoomSourceState = z.output<typeof RoomSourceState>

// Room shapes ---------------------------------------------------------------------------------------------------------

/** A token's minimum for `token_gated`: base units of the token (wei for an 18-decimal token). */
export const RoomTokenGate = z.object({ kind: z.literal('token'), token: Address, minimum: WeiString }).strict()
/** The agent whose active followers may join (`followers`); the room owner's own agent. */
export const RoomFollowGate = z.object({ kind: z.literal('follow'), agentId: Uuid }).strict()
/** The person whose access token gates (`user_token_gated`). */
export const RoomUserTokenGate = z.object({ kind: z.literal('user_token'), userId: Uuid }).strict()
export const RoomGate = z.discriminatedUnion('kind', [RoomTokenGate, RoomFollowGate, RoomUserTokenGate])
export type RoomGate = z.output<typeof RoomGate>

/** The gate kind each privacy needs; `public` and `private` take none. */
export const GATE_OF_PRIVACY = {
  public: null,
  private: null,
  followers: 'follow',
  token_gated: 'token',
  user_token_gated: 'user_token',
} as const satisfies Record<RoomPrivacy, RoomGate['kind'] | null>

export const RoomSettings = z
  .object({
    /** Who may post: every member, or the owner and admins only (an announcement room). */
    whoMayPost: RoomAudience,
    /** Who may make invites. */
    whoMayInvite: RoomAudience,
    /** Seconds a member waits between two messages (0 = off); the owner and admins are exempt. */
    slowModeS: z.number().int().min(0).max(ROOM_SLOW_MODE_MAX_S),
    /** Whether agents may be members. */
    agentsAllowed: z.boolean(),
    /** Members at most (people and agents). */
    maxMembers: z.number().int().min(2).max(ROOM_MEMBERS_MAX),
  })
  .strict()
export type RoomSettings = z.output<typeof RoomSettings>
export const DEFAULT_ROOM_SETTINGS: RoomSettings = { whoMayPost: 'members', whoMayInvite: 'admins', slowModeS: 0, agentsAllowed: true, maxMembers: ROOM_MEMBERS_MAX }

/** A person as rooms show them. */
export const RoomUser = z.object({ id: Uuid, handle: Handle.nullable(), displayName: z.string(), avatarUrl: z.string().nullable() })
export type RoomUser = z.output<typeof RoomUser>

/** The caller's own membership of a room. */
export const RoomMe = z.object({
  memberId: Uuid,
  role: RoomRole,
  joinedAt: IsoTimestamp,
  /** The highest seq the caller has read. */
  lastReadSeq: z.number().int().nonnegative(),
  /** Messages after `lastReadSeq` (the room's `lastSeq` minus it; system messages count). */
  unread: z.number().int().nonnegative(),
  /** Mentions of the caller and replies to the caller after `lastReadSeq`. */
  unreadMentions: z.number().int().nonnegative(),
  /** Notifications of this room are held until then (null = not muted; far future = until unmuted). */
  mutedUntil: IsoTimestamp.nullable(),
  notify: RoomNotifyLevel,
})
export type RoomMe = z.output<typeof RoomMe>

/** The room's platform source, shown to its owner and admins only (null for everyone else). */
export const RoomSource = z.object({
  state: RoomSourceState,
  /** `src_…`, the id a contract reads it by: `account_source("src_…")`. Null until registered. */
  sourceId: z.string().regex(/^src_[0-9a-hjkmnp-tv-z]{26}$/).nullable(),
  /** The fields every forwarded message carries, in order. */
  fields: z.array(z.object({ name: z.string(), type: z.string() })),
  /** The highest seq the platform took (its receipt stored); null before the first. */
  forwardedThroughSeq: z.number().int().nonnegative().nullable(),
  /** Messages waiting to be forwarded (deleted ones are skipped, not counted). */
  pending: z.number().int().nonnegative(),
  /** Why it is `unavailable`, or the last retryable failure, in plain words; null when none. */
  reason: z.string().nullable(),
})
export type RoomSource = z.output<typeof RoomSource>

/** The newest message as a room list shows it. */
export const RoomLastMessage = z.object({
  seq: z.number().int().positive(),
  kind: RoomMessageKind,
  /** The author's name as shown (a person's display name, an agent's name, or "algo.boo" for system). */
  authorName: z.string(),
  /** At most 140 characters of the body; empty for a deleted message. */
  preview: z.string(),
  deleted: z.boolean(),
  createdAt: IsoTimestamp,
})
export type RoomLastMessage = z.output<typeof RoomLastMessage>

export const Room = z.object({
  id: Uuid,
  kind: RoomKind,
  /** For a direct message, the other person's display name as the caller sees it. */
  name: z.string(),
  /** Unique, `^[a-z0-9][a-z0-9-]{1,47}$`; null for a direct message. */
  slug: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  topic: z.string().nullable(),
  privacy: RoomPrivacy,
  gate: RoomGate.nullable(),
  owner: RoomUser,
  /** The agent a `launch` or `followers` room is about. */
  agent: AgentSummary.nullable(),
  /** The other person of a direct message, else null. */
  peer: RoomUser.nullable(),
  settings: RoomSettings,
  memberCount: z.number().int().nonnegative(),
  messageCount: z.number().int().nonnegative(),
  /** The newest seq (0 when empty). */
  lastSeq: z.number().int().nonnegative(),
  lastMessage: RoomLastMessage.nullable(),
  lastMessageAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
  archivedAt: IsoTimestamp.nullable(),
  /** Null when the caller is not a member. */
  me: RoomMe.nullable(),
  /** Owner and admins only; null for everyone else. */
  source: RoomSource.nullable(),
})
export type Room = z.output<typeof Room>

/** A room seen from outside (discovery, an invite's preview): no messages, no members. */
export const RoomPreview = Room.pick({ id: true, kind: true, name: true, slug: true, avatarUrl: true, topic: true, privacy: true, gate: true, owner: true, agent: true, memberCount: true, lastMessageAt: true, createdAt: true, archivedAt: true })
export type RoomPreview = z.output<typeof RoomPreview>

export const RoomMember = z.object({
  id: Uuid,
  roomId: Uuid,
  kind: RoomMemberKind,
  user: RoomUser.nullable(),
  agent: AgentSummary.nullable(),
  role: RoomRole,
  joinedAt: IsoTimestamp,
  invitedBy: Uuid.nullable(),
  /** Owner and admins see bans; for anyone else a banned member is not listed. */
  bannedAt: IsoTimestamp.nullable(),
  participation: RoomAgentParticipation.nullable().default(null),
})
export type RoomMember = z.output<typeof RoomMember>

export const RoomInvite = z.object({
  code: z.string().regex(/^[A-Za-z0-9_-]{12,32}$/),
  roomId: Uuid,
  createdBy: Uuid,
  /** Only this person may use it (a direct invite); null = anyone with the code. */
  forUserId: Uuid.nullable(),
  uses: z.number().int().nonnegative(),
  /** Null = no limit. */
  maxUses: z.number().int().positive().nullable(),
  expiresAt: IsoTimestamp.nullable(),
  revokedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
})
export type RoomInvite = z.output<typeof RoomInvite>

// Messages ------------------------------------------------------------------------------------------------------------

const HttpsUrl = z.url({ protocol: /^https$/ }).max(2000)

/** One image on a `media` message: an upload's answer (`POST /rooms/:id/media`) passed back as is. */
export const RoomMedia = z
  .object({
    url: z.string().min(1).max(2000),
    contentType: z.enum(['image/png', 'image/jpeg', 'image/gif', 'image/webp']),
    bytes: z.number().int().positive().max(ROOM_MEDIA_MAX_BYTES),
  })
  .strict()
export type RoomMedia = z.output<typeof RoomMedia>

/** `payload` of each kind; the server checks it against this map. */
export const RoomMessagePayloads = {
  text: z.object({}).strict(),
  agent_proposal: z.object({ proposalId: Uuid, kind: z.enum(['watch', 'research']), text: z.string().max(1000) }).strict(),
  media: z.object({ media: z.array(RoomMedia).min(1).max(ROOM_MEDIA_MAX) }).strict(),
  /** A link as the writer gave it; algo.boo fetches nothing from it. */
  link: z
    .object({ url: HttpsUrl, title: z.string().trim().max(200).optional(), description: z.string().trim().max(500).optional(), imageUrl: HttpsUrl.optional() })
    .strict(),
  token_card: z.object({ token: Address }).strict(),
  user_card: z.object({ userId: Uuid }).strict(),
  /** A member's call on a token (product rule 8: an output like an order, never an order). */
  callout: z.object({ token: Address, stance: z.enum(['buy', 'sell', 'watch']), note: z.string().trim().max(280).optional() }).strict(),
  /** Declared for the wallets that come later; posting one is refused (`ROOM_KIND_NOT_OFFERED`). */
  airdrop: z.object({ token: Address, amount: WeiString, recipients: z.number().int().positive().max(ROOM_MEMBERS_MAX) }).strict(),
  /** Written by the server: what happened, who did it and to whom. */
  system: z
    .object({
      event: RoomEventKind,
      actorUserId: Uuid.nullable(),
      subjectUserId: Uuid.nullable(),
      subjectAgentId: Uuid.nullable(),
      seq: z.number().int().positive().nullable(),
    })
    .strict(),
} as const satisfies Record<RoomMessageKind, z.ZodType>

export const RoomMentions = z
  .object({
    users: z.array(Uuid).max(ROOM_MENTIONS_MAX).default([]),
    agents: z.array(Uuid).max(ROOM_MENTIONS_MAX).default([]),
    tokens: z.array(Address).max(ROOM_MENTIONS_MAX).default([]),
  })
  .strict()
export type RoomMentions = z.output<typeof RoomMentions>

export const RoomReaction = z.object({ emoji: z.string(), count: z.number().int().positive(), mine: z.boolean() })
export type RoomReaction = z.output<typeof RoomReaction>

export const RoomAuthor = z.object({ kind: RoomAuthorKind, user: RoomUser.nullable(), agent: AgentSummary.nullable() })
export type RoomAuthor = z.output<typeof RoomAuthor>

export const RoomMessage = z.object({
  roomId: Uuid,
  /** Dense per room (1, 2, 3 …), the order of commit; the stream's `Last-Event-ID`. */
  seq: z.number().int().positive(),
  author: RoomAuthor,
  kind: RoomMessageKind,
  /** Empty once deleted. */
  body: z.string(),
  /** The kind's payload (`RoomMessagePayloads`); `{}` once deleted. */
  payload: JsonObject,
  replyToSeq: z.number().int().positive().nullable(),
  mentions: z.object({ users: z.array(Uuid), agents: z.array(Uuid), tokens: z.array(Address) }),
  reactions: z.array(RoomReaction),
  pinned: z.boolean(),
  editedAt: IsoTimestamp.nullable(),
  deletedAt: IsoTimestamp.nullable(),
  createdAt: IsoTimestamp,
})
export type RoomMessage = z.output<typeof RoomMessage>

// Requests ------------------------------------------------------------------------------------------------------------

export const RoomParams = z.object({ id: Uuid })
export const RoomSlugParams = z.object({ slug: z.string().regex(/^[a-z0-9][a-z0-9-]{1,47}$/) })
export const RoomMessageParams = z.object({ id: Uuid, seq: z.coerce.number().int().positive() })
export const RoomMemberParams = z.object({ id: Uuid, memberId: Uuid })
export const RoomInviteParams = z.object({ id: Uuid, code: RoomInvite.shape.code })
export const RoomInviteCodeParams = z.object({ code: RoomInvite.shape.code })

export const RoomSlug = RoomSlugParams.shape.slug
const RoomName = z.string().trim().min(1).max(80)
const RoomTopic = z.string().trim().max(280)
const RoomAvatarUrl = z.string().trim().min(1).max(2000)

export const ListRoomsQuery = z.object({
  /** `mine`: rooms the caller is a member of, most recently active first; `discover`: public rooms by activity. */
  scope: z.enum(['mine', 'discover']).default('mine'),
  kind: RoomKind.optional(),
  /** `active` (default), `archived` or `all` (mine only; discover lists active rooms). */
  state: z.enum(['active', 'archived', 'all']).default('active'),
  /** Name or slug starts with (case-insensitive). */
  q: z.string().trim().min(1).max(80).optional(),
  cursor: Cursor.optional(),
  limit: z.coerce.number().int().min(1).max(ROOM_PAGE_MAX).default(50),
})

const SharedRoomFields = {
  name: RoomName,
  slug: RoomSlug.optional(),
  topic: RoomTopic.optional(),
  avatarUrl: RoomAvatarUrl.optional(),
  privacy: RoomPrivacy.default('public'),
  gate: RoomGate.optional(),
  settings: RoomSettings.partial().optional(),
}

/**
 * A direct message names the other person (an existing one is answered with 200); the other kinds name the room.
 * A `launch` room names the agent (the caller's own); a `followers` room's gate names the caller's own agent.
 */
export const CreateRoomBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('dm'), userId: Uuid }).strict(),
  z.object({ kind: z.literal('group'), ...SharedRoomFields }).strict(),
  z.object({ kind: z.literal('personal'), ...SharedRoomFields }).strict(),
  z.object({ kind: z.literal('launch'), agentId: Uuid, ...SharedRoomFields }).strict(),
])
export type CreateRoomBody = z.output<typeof CreateRoomBody>

export const PatchRoomBody = z
  .object({
    name: RoomName.optional(),
    slug: RoomSlug.optional(),
    topic: RoomTopic.nullable().optional(),
    avatarUrl: RoomAvatarUrl.nullable().optional(),
    privacy: RoomPrivacy.optional(),
    gate: RoomGate.nullable().optional(),
    settings: RoomSettings.partial().optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length > 0, 'nothing to change')
export type PatchRoomBody = z.output<typeof PatchRoomBody>

export const ListRoomMembersQuery = z.object({
  role: RoomRole.optional(),
  kind: RoomMemberKind.optional(),
  /** Handle or name starts with (case-insensitive). */
  q: z.string().trim().min(1).max(80).optional(),
  /** Owner and admins only: the banned members instead. */
  banned: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
  cursor: Cursor.optional(),
  limit: z.coerce.number().int().min(1).max(ROOM_PAGE_MAX).default(50),
})

export const JoinRoomBody = z.object({ inviteCode: RoomInvite.shape.code.optional() }).strict()
export type JoinRoomBody = z.output<typeof JoinRoomBody>

/** `forUserId`: a direct invite to one person (they are notified); else a code anyone may use. */
export const CreateRoomInviteBody = z
  .object({
    forUserId: Uuid.optional(),
    maxUses: z.number().int().min(1).max(ROOM_MEMBERS_MAX).optional(),
    /** Seconds from now; at most 30 days. */
    expiresInS: z.number().int().min(60).max(30 * 86_400).optional(),
  })
  .strict()
export type CreateRoomInviteBody = z.output<typeof CreateRoomInviteBody>

/** An agent the caller created, added to the room by its owner (the agent says nothing until agents in rooms are built). */
export const AddRoomAgentBody = z.object({ agentId: Uuid }).strict()
export type AddRoomAgentBody = z.output<typeof AddRoomAgentBody>

export const SetRoomRoleBody = z.object({ role: z.enum(['admin', 'member']) }).strict()
export type SetRoomRoleBody = z.output<typeof SetRoomRoleBody>

export const ModerateRoomMemberBody = z.object({ reason: z.string().trim().min(1).max(280).optional() }).strict()
export type ModerateRoomMemberBody = z.output<typeof ModerateRoomMemberBody>

export const RoomMessagesQuery = z
  .object({
    /** Messages with seq below this, newest first. */
    before: z.coerce.number().int().positive().optional(),
    /** Messages with seq above this (the `limit` right after it), still newest first. */
    after: z.coerce.number().int().nonnegative().optional(),
    limit: z.coerce.number().int().min(1).max(ROOM_PAGE_MAX).default(50),
  })
  .refine((q) => q.before === undefined || q.after === undefined, 'give before or after, not both')

/** A hard bound on the request; the server refuses a body over `ROOM_MESSAGE_MAX_CHARS` with `ROOM_MESSAGE_TOO_LONG`. */
const MessageBodyText = z.string().max(ROOM_MESSAGE_MAX_CHARS * 4)
const PostCommon = { body: MessageBodyText.default(''), replyToSeq: z.number().int().positive().optional(), mentions: RoomMentions.optional() }

/**
 * One message. `text` needs a body of 1 to 4 000 characters after trimming; the others may carry one as a caption.
 * `system` is never accepted. Stored with the request's `Idempotency-Key`, so a retry is the same message.
 */
export const PostRoomMessageBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), ...PostCommon, payload: RoomMessagePayloads.text.default({}) }).strict(),
  z.object({ kind: z.literal('media'), ...PostCommon, payload: RoomMessagePayloads.media }).strict(),
  z.object({ kind: z.literal('link'), ...PostCommon, payload: RoomMessagePayloads.link }).strict(),
  z.object({ kind: z.literal('token_card'), ...PostCommon, payload: RoomMessagePayloads.token_card }).strict(),
  z.object({ kind: z.literal('user_card'), ...PostCommon, payload: RoomMessagePayloads.user_card }).strict(),
  z.object({ kind: z.literal('callout'), ...PostCommon, payload: RoomMessagePayloads.callout }).strict(),
  z.object({ kind: z.literal('airdrop'), ...PostCommon, payload: RoomMessagePayloads.airdrop }).strict(),
])
export type PostRoomMessageBody = z.output<typeof PostRoomMessageBody>

/** The author's edit: the body (and mentions); the kind and payload stay. */
export const EditRoomMessageBody = z.object({ body: MessageBodyText, mentions: RoomMentions.optional() }).strict()
export type EditRoomMessageBody = z.output<typeof EditRoomMessageBody>

/** One emoji (a grapheme of pictographs, joiners and modifiers). */
export const RoomEmoji = z
  .string()
  .min(1)
  .max(32)
  // Emoji_Component holds the joiner (U+200D), the emoji variation selector (U+FE0F) and the keycap (U+20E3).
  .regex(/^[\p{Extended_Pictographic}\p{Emoji_Component}]+$/u, 'one emoji')
export const RoomReactionBody = z.object({ emoji: RoomEmoji }).strict()
export type RoomReactionBody = z.output<typeof RoomReactionBody>

export const RoomMentionsQuery = z.object({
  /** Handle, name or token symbol starts with (case-insensitive); empty lists the room's most recent writers. */
  q: z.string().trim().max(40).default(''),
  limit: z.coerce.number().int().min(1).max(20).default(8),
})

export const RoomSearchQuery = z.object({
  /** Words of the message body (Postgres full text, simple dictionary). */
  q: z.string().trim().min(1).max(200),
  cursor: Cursor.optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
})

export const RoomReadBody = z.object({ lastReadSeq: z.number().int().nonnegative() }).strict()
export type RoomReadBody = z.output<typeof RoomReadBody>

/** `mutedUntil` null unmutes; a far-future time mutes until unmuted. `notify` sets the level at the same time. */
export const RoomMuteBody = z
  .object({ mutedUntil: IsoTimestamp.nullable().optional(), notify: RoomNotifyLevel.optional() })
  .strict()
  .refine((b) => b.mutedUntil !== undefined || b.notify !== undefined, 'nothing to change')
export type RoomMuteBody = z.output<typeof RoomMuteBody>

export const RoomStreamQuery = z.object({
  /** Resume after this seq (the SSE `Last-Event-ID` header wins when both are given). */
  after: z.coerce.number().int().nonnegative().optional(),
})

// Responses -----------------------------------------------------------------------------------------------------------

export const RoomPage = z.object({ items: z.array(Room), nextCursor: Cursor.nullable() })
export type RoomPage = z.output<typeof RoomPage>

export const RoomDetail = Room.extend({
  /** Pinned messages, newest pin first. */
  pins: z.array(RoomMessage),
})
export type RoomDetail = z.output<typeof RoomDetail>

export const RoomMemberPage = z.object({ items: z.array(RoomMember), nextCursor: Cursor.nullable() })
export type RoomMemberPage = z.output<typeof RoomMemberPage>

export const RoomInvites = z.object({ items: z.array(RoomInvite) })
export type RoomInvites = z.output<typeof RoomInvites>

/** `GET /room-invites/:code`: what the code opens, and whether the caller may still use it. */
export const RoomInvitePreview = z.object({
  room: RoomPreview,
  invite: RoomInvite.pick({ code: true, expiresAt: true, maxUses: true, uses: true }),
  /** Null when the caller may join with it; else the refusal code joining would meet. */
  refusal: z.string().nullable(),
})
export type RoomInvitePreview = z.output<typeof RoomInvitePreview>

export const RoomMessagePage = z.object({
  /** Newest first. */
  items: z.array(RoomMessage),
  /** Pass as `before` for older messages; null at the room's first message. */
  nextBefore: z.number().int().positive().nullable(),
  lastSeq: z.number().int().nonnegative(),
})
export type RoomMessagePage = z.output<typeof RoomMessagePage>

export const RoomPins = z.object({ items: z.array(RoomMessage) })
export type RoomPins = z.output<typeof RoomPins>

export const RoomReactionsOf = z.object({ roomId: Uuid, seq: z.number().int().positive(), reactions: z.array(RoomReaction) })
export type RoomReactionsOf = z.output<typeof RoomReactionsOf>

export const RoomMentionCandidates = z.object({
  users: z.array(RoomUser),
  agents: z.array(AgentSummary),
  tokens: z.array(z.object({ address: Address, symbol: z.string().nullable(), agentHandle: Handle.nullable() })),
})
export type RoomMentionCandidates = z.output<typeof RoomMentionCandidates>

export const RoomSearchHit = z.object({ room: RoomPreview, message: RoomMessage, /** The matching words marked `<b>…</b>` in plain text. */ snippet: z.string() })
export type RoomSearchHit = z.output<typeof RoomSearchHit>
export const RoomSearchPage = z.object({ items: z.array(RoomSearchHit), nextCursor: Cursor.nullable() })
export type RoomSearchPage = z.output<typeof RoomSearchPage>

export const RoomReadState = z.object({ roomId: Uuid, lastReadSeq: z.number().int().nonnegative(), unread: z.number().int().nonnegative(), unreadMentions: z.number().int().nonnegative() })
export type RoomReadState = z.output<typeof RoomReadState>

export const RoomLeft = z.object({ ok: z.literal(true), roomId: Uuid })
export type RoomLeft = z.output<typeof RoomLeft>

export const RoomTyping = z.object({ ok: z.literal(true), ttlMs: z.number().int().positive() })
export type RoomTyping = z.output<typeof RoomTyping>

/**
 * One event of `GET /rooms/:id/stream`. SSE `event:` names the variant; `id:` is always the highest message seq
 * delivered so far, so `Last-Event-ID` resumes the messages without gaps (a reconnect reads the table from it).
 * `message` is a new message; `message_updated` an edit, a deletion or a pin change of one already delivered;
 * `reactions` a message's reactions now; `typing` and `presence` are ephemeral (never stored, dropped under
 * backpressure, a client forgets them after `ttlMs`); `member` a join, leave, role change, kick or ban; `room` the room
 * itself changed (settings, archive). `closed` ends the stream: the caller left, was removed or banned.
 */
export const RoomStreamEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('message'), message: RoomMessage }),
  z.object({ type: z.literal('message_updated'), message: RoomMessage }),
  z.object({ type: z.literal('reactions'), roomId: Uuid, seq: z.number().int().positive(), reactions: z.array(RoomReaction) }),
  z.object({ type: z.literal('typing'), roomId: Uuid, user: RoomUser, ttlMs: z.number().int().positive() }),
  z.object({ type: z.literal('presence'), roomId: Uuid, users: z.array(RoomUser), ttlMs: z.number().int().positive() }),
  z.object({ type: z.literal('member'), roomId: Uuid, event: RoomEventKind, member: RoomMember }),
  z.object({ type: z.literal('room'), room: Room }),
  z.object({ type: z.literal('closed'), roomId: Uuid, reason: z.enum(['left', 'kicked', 'banned', 'archived']) }),
])
export type RoomStreamEvent = z.output<typeof RoomStreamEvent>

/**
 * One event of `GET /rooms/stream`, the caller's room list: `room` whenever one of the caller's rooms changes (a new
 * message, its unread counts, its settings), `removed` when the caller is no longer a member. `id:` is a counter of
 * this connection only; a reconnect reloads `GET /rooms` (the list is small and current).
 */
export const RoomListStreamEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('room'), room: Room }),
  z.object({ type: z.literal('removed'), roomId: Uuid, reason: z.enum(['left', 'kicked', 'banned', 'archived']) }),
])
export type RoomListStreamEvent = z.output<typeof RoomListStreamEvent>

// The room's feed as a platform source (capability 2) ---------------------------------------------------------------

/**
 * The declared fields of every room's source on the platform, in order (`POST /v2/venues/rh/4663/data/sources`, kind
 * `webhook`). Each forwarded message carries: `author` (`user:<id>` or `agent:<id>`), `kind`, `body` (the text, at most
 * 4 000 characters), `mentions` (the mentioned ids and tokens, comma-joined), `token` (the token a card, callout or the
 * first token mention names, else absent), `seq`, and `at` (`occurred_at`, the message's time).
 */
export const ROOM_SOURCE_FIELDS = [
  { name: 'author', type: 'text' },
  { name: 'kind', type: 'text' },
  { name: 'body', type: 'text' },
  { name: 'mentions', type: 'text' },
  { name: 'token', type: 'token' },
  { name: 'seq', type: 'number' },
  { name: 'occurred_at', type: 'time' },
] as const

// Job payloads (docs/RUNTIME.md "Rooms") ------------------------------------------------------------------------------

/** `rooms.source`: register the room's private platform source (once; retried until registered or refused for good). */
export const RoomSourceJobPayload = z.object({ roomId: Uuid })
export type RoomSourceJobPayload = z.output<typeof RoomSourceJobPayload>
/** `rooms.notify`: the notifications one message causes (mentions, a reply, a direct message, a pin of a message). */
export const RoomNotifyJobPayload = z.object({ roomId: Uuid, seq: z.number().int().positive(), event: z.enum(['message', 'pin', 'invite']), inviteCode: z.string().optional(), actorUserId: Uuid.nullable().optional() })
export type RoomNotifyJobPayload = z.output<typeof RoomNotifyJobPayload>

// Refusals (permanent wire values, `ROOM_REFUSAL_CODES` in ../refusals.ts) --------------------------------------------

/** Each room refusal in one plain sentence: the API's `error.message` and the web's copy say the same. */
export const ROOM_REFUSAL_WORDS = {
  ROOM_NOT_MEMBER: 'You are not a member of this room. Join it first.',
  ROOM_GATE_NOT_MET: 'You do not meet this room’s entry rule yet; it says what it asks for.',
  ROOM_GATE_UNCHECKABLE: 'This room’s entry rule could not be checked just now. Try again in a minute.',
  ROOM_GATE_NOT_OFFERED: 'This entry rule is not offered yet, so nobody can join by it.',
  ROOM_INVITE_REQUIRED: 'This room is private. You need an invite to join it.',
  ROOM_INVITE_INVALID: 'This invite has expired, is used up, was withdrawn or is for someone else.',
  ROOM_BANNED: 'You were banned from this room, so you cannot join or write in it.',
  ROOM_ARCHIVED: 'This room is archived. It can be read, but nothing new can be written.',
  ROOM_FULL: 'This room is full. It takes no more members.',
  ROOM_LIMIT_REACHED: 'You already own as many rooms as one person may. Archive one to make another.',
  ROOM_SLOW_MODE: 'This room has slow mode on. Wait a moment before your next message.',
  ROOM_MESSAGE_TOO_LONG: 'This message is longer than 4,000 characters. Shorten it or split it.',
  ROOM_POST_NOT_ALLOWED: 'Only the room’s owner and admins can write here.',
  ROOM_ROLE_REQUIRED: 'Only the room’s owner or an admin can do this.',
  ROOM_DM_NOT_ALLOWED: 'You can only message people you follow, who follow you, or who share a room with you.',
  ROOM_KIND_NOT_OFFERED: 'This kind of message is not offered yet.',
  ROOM_SLUG_TAKEN: 'Another room already uses this address. Pick another one.',
  ROOM_MESSAGE_DELETED: 'This message was deleted, so it cannot be changed.',
  ROOM_AGENT_NOT_ALLOWED: 'This agent cannot be added: the room takes no agents, or the agent is not yours.',
  ROOM_PIN_LIMIT: 'This room already has 50 pinned messages. Unpin one first.',
  ROOM_OWNER_CANNOT_LEAVE: 'The owner cannot leave the room. Archive it instead.',
} as const satisfies Record<RoomRefusalCode, string>
